import * as React from 'react';
import { connect } from 'react-redux';

import { CompactInlineAlertWidget, InlineAlertType } from '@tableau/widgets';

import { ICollectionInfo } from './ICollectionInfo';
import { L10n } from '../../../ts/L10n/L10n';
import { Messages, Formatters } from '../../../codegen/Localize';
import { ContentDetailSection } from '../../content/contentDetailDialog/ContentDetailSection';
import { ConnectedOwnerDetailSection } from '../../content/contentDetailDialog/OwnerDetailSection';
import { CreatedDetailSection, ModifiedDetailSection } from '../../content/contentDetailDialog/CommonContentDetailSections';
import { ConnectedDeleteContentSection } from '../../content/contentDetailDialog/DeleteContentSection';
import { ConnectedDescriptionDetailSection } from '../../content/contentDetailDialog/DescriptionDetailSection';
import { MapStateToProps } from '../../utils/ConnectUtils';
import { AppState } from '../../state/AppState';
import { CompositeSessionInfoSelectors } from '../../selectors/SessionInfoSelectors';
import { CollectionsResourceQuery } from './Collections';

export interface CollectionItemCountSectionProps {
  readonly collection: ICollectionInfo;
}

export function CollectionItemCountSection(props: CollectionItemCountSectionProps): JSX.Element {
  const hiddenItemCount = props.collection.totalItemCount - props.collection.permissionedItemCount;
  const detail: JSX.Element =
    <>
      {L10n.localizedCount(props.collection.totalItemCount)}
      { hiddenItemCount > 0 &&
        <CompactInlineAlertWidget
          testId={'collection-detail-count'}
          type={InlineAlertType.Info}
          style={{ marginTop: '8px'}}
        >
          {Messages.collectionsDetailsItemCountPermissionedWarning({ITEM_COUNT: hiddenItemCount, ITEM_COUNT_LOC: Formatters.formatCount(hiddenItemCount)})}
        </CompactInlineAlertWidget>
      }
    </>;

  return (
    <ContentDetailSection
      header={Messages.collectionsDetailsItemCount()}
      detail={detail}
    />
  );
}

export interface CollectionDetailsDialogBodyOwnProps {
  readonly collection: ICollectionInfo;
}

export interface CollectionDetailsDialogBodyStateProps {
  readonly isAdminOrOwner: boolean;
}

export type CollectionDetailsDialogBodyProps = CollectionDetailsDialogBodyOwnProps & CollectionDetailsDialogBodyStateProps;

export function CollectionDetailsDialogBody(props: CollectionDetailsDialogBodyProps): JSX.Element {

  const setDescription = React.useCallback((description: string) => {
    const updatedCollectionData = { luid: props.collection.luid, description };
    return new CollectionsResourceQuery().update([updatedCollectionData]); // DescriptionDetailSection handles erorrs
  }, [props.collection]);

  return (
    <>
      <ConnectedDescriptionDetailSection
        content={props.collection}
        isActionEnabled={props.isAdminOrOwner}
        setDescription={setDescription}
      />
      <ConnectedOwnerDetailSection content={props.collection} isActionEnabled={props.isAdminOrOwner}/>
      <CollectionItemCountSection collection={props.collection} />
      <CreatedDetailSection content={props.collection} />
      <ModifiedDetailSection content={props.collection} />
      {props.isAdminOrOwner &&
        <ConnectedDeleteContentSection
          actionMessage={Messages.collectionsDetailsDelete()}
          content={props.collection}
        />
      }
    </>
  );
}

const mapStateToProps: MapStateToProps<CollectionDetailsDialogBodyStateProps> = (state: AppState, ownProps: CollectionDetailsDialogBodyOwnProps) => ({
  isAdminOrOwner: CompositeSessionInfoSelectors.currentUserIsAdminOrOwnerSelector(ownProps.collection.owner)(state)
});

export const ConnectedCollectionDetailsDialogBody: React.ComponentClass<CollectionDetailsDialogBodyOwnProps> =
  connect(mapStateToProps)(CollectionDetailsDialogBody);
